import { useState, useEffect, useCallback } from 'react';
import {
  PlayerProfile,
  ActiveRollInfo,
  AllowanceStatus,
  getPlayerProfile,
  getActiveRolls,
  getUserAlbum,
  getAllowanceStatus,
  claimAllowance,
  claimSetReward,
  calculateAllowanceAmount
} from '../lib/gameEngine';
import { SetSeriesItem } from '../lib/albumData';
import { supabase } from '../lib/supabase';

export function usePlayerSession() {
  const [profile, setProfile] = useState<PlayerProfile>({
    wallet: 200.00,
    dailyFreeRollsLeft: 3,
    lastDailyReset: new Date().toISOString(),
    lastAllowanceAt: new Date(Date.now() - 12 * 3600 * 1000).toISOString(),
    totalRollsHunted: 0,
    isGuest: true
  });
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [activeRolls, setActiveRolls] = useState<Record<string, ActiveRollInfo>>({});
  const [setsData, setSetsData] = useState<SetSeriesItem[]>([]);
  const [userAlbum, setUserAlbum] = useState<Record<string, number>>({});
  const [allowanceStatus, setAllowanceStatus] = useState<AllowanceStatus | null>(null);
  const [isInitialLoaded, setIsInitialLoaded] = useState<boolean>(false);

  const refreshProfileAndRolls = useCallback(async () => {
    try {
      const [prof, rolls, albumRes, allowStatus] = await Promise.all([
        getPlayerProfile(),
        getActiveRolls(),
        getUserAlbum(),
        getAllowanceStatus()
      ]);
      setProfile(prof);
      setActiveRolls(rolls);
      if (albumRes.sets && albumRes.sets.length > 0) {
        setSetsData(albumRes.sets);
      }
      if (albumRes.userAlbum) {
        setUserAlbum(albumRes.userAlbum);
      }
      if (allowStatus) {
        setAllowanceStatus(allowStatus);
      } else {
        // Fallback local calculation
        const distinctIds = Object.keys(albumRes.userAlbum || {}).filter(k => (albumRes.userAlbum?.[k] || 0) > 0);
        const distinctCount = distinctIds.length;
        const collectionFaceValue = distinctCount * 2.00; // Estimated 2€ default fallback
        const lastAt = prof.lastAllowanceAt ? new Date(prof.lastAllowanceAt).getTime() : Date.now() - 12 * 3600 * 1000;
        const nextAt = lastAt + 12 * 3600 * 1000;
        const now = Date.now();
        const diffSecs = Math.max(0, Math.floor((nextAt - now) / 1000));
        setAllowanceStatus({
          distinctCoins: distinctCount,
          collectionFaceValue,
          allowanceAmount: calculateAllowanceAmount(collectionFaceValue),
          isClaimable: diffSecs === 0,
          lastAllowanceAt: new Date(lastAt).toISOString(),
          nextAllowanceAt: new Date(nextAt).toISOString(),
          secondsRemaining: diffSecs
        });
      }
      setIsInitialLoaded(true);
    } catch (e) {
      console.error('Failed to refresh session data:', e);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshProfileAndRolls();
  }, [refreshProfileAndRolls]);

  // Allowance 1-second countdown ticker
  useEffect(() => {
    const timer = setInterval(() => {
      setAllowanceStatus(prev => {
        if (!prev) return prev;
        if (prev.isClaimable) return prev;
        if (prev.secondsRemaining <= 1) {
          return {
            ...prev,
            secondsRemaining: 0,
            isClaimable: true
          };
        }
        return {
          ...prev,
          secondsRemaining: prev.secondsRemaining - 1
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Supabase Auth State Synchronization
  useEffect(() => {
    let initialUserId: string | null | undefined = undefined;

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user && !session.user.is_anonymous) {
          initialUserId = session.user.id;
          const meta = session.user.user_metadata || {};
          const name = meta.full_name || meta.name || meta.preferred_username || meta.user_name || meta.custom_claims?.global_name || 'Player';
          setUsername(name);
        } else if (session?.user) {
          initialUserId = session.user.id;
        } else {
          initialUserId = null;
        }
      } catch (e) {
        console.error('Error checking auth session:', e);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        window.location.href = '/';
        return;
      }

      if (event === 'SIGNED_IN' && session?.user && !session.user.is_anonymous) {
        // If an authenticated user signs in or user identity changes, forcibly reload the page
        if (initialUserId !== undefined && initialUserId !== session.user.id) {
          window.location.href = '/';
          return;
        }
        initialUserId = session.user.id;
      }

      if (session && session.user && !session.user.is_anonymous) {
        const meta = session.user.user_metadata || {};
        const name = meta.full_name || meta.name || meta.preferred_username || meta.user_name || meta.custom_claims?.global_name || 'Player';
        setUsername(name);
      } else {
        setUsername(undefined);
      }
      refreshProfileAndRolls();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshProfileAndRolls]);

  const handleClaimAllowance = useCallback(async () => {
    try {
      const res = await claimAllowance();
      if (res.success) {
        if (res.newWallet !== undefined) {
          setProfile(p => ({ ...p, wallet: res.newWallet! }));
        }
        refreshProfileAndRolls();
        return { success: true, amountClaimed: res.amountClaimed };
      } else {
        return { success: false, error: res.error };
      }
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to claim allowance' };
    }
  }, [refreshProfileAndRolls]);

  const handleClaimSetReward = useCallback(async (setId: string, rewardType: 'circulation' | 'rare' = 'circulation') => {
    try {
      const res = await claimSetReward(setId, rewardType);
      if (res.success) {
        if (res.newWallet !== undefined) {
          setProfile(p => ({ ...p, wallet: res.newWallet! }));
        }
        refreshProfileAndRolls();
        return { success: true, rewardAmount: res.rewardAmount };
      } else {
        return { success: false, error: res.error };
      }
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to claim set reward' };
    }
  }, [refreshProfileAndRolls]);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err: unknown) {
      console.error('Failed to sign out:', err);
    } finally {
      window.location.href = '/';
    }
  }, []);

  return {
    profile,
    setProfile,
    username,
    activeRolls,
    setActiveRolls,
    setsData,
    setSetsData,
    userAlbum,
    setUserAlbum,
    allowanceStatus,
    isInitialLoaded,
    handleClaimAllowance,
    handleClaimSetReward,
    refreshProfileAndRolls,
    handleSignOut
  };
}
